import React, { useState, useEffect } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';

export function PWAUpdateNotification() {
  const [showUpdate, setShowUpdate] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Check for updates more frequently (every 30 seconds when active)
      const checkForUpdates = () => {
        navigator.serviceWorker.ready.then((reg) => {
          reg.update().catch((error) => {
            console.log('Update check failed:', error);
          });
        });
      };

      // Check immediately on mount
      checkForUpdates();

      // Check every 30 seconds (more aggressive)
      const interval = setInterval(checkForUpdates, 30 * 1000); // 30 seconds

      // Listen for new service worker waiting to activate
      navigator.serviceWorker.ready.then((reg) => {
        setRegistration(reg);

        if (reg.waiting) {
          console.log('⚠️ Service worker update waiting - showing notification');
          setShowUpdate(true);
        }

        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          console.log('🔍 New service worker detected');
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              console.log('🔄 Service worker state:', newWorker.state);
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New service worker is installed but waiting
                console.log('✅ New version ready - showing update notification');
                setShowUpdate(true);
                setRegistration(reg);
                
                // Auto-update after 10 seconds if user doesn't interact
                setTimeout(() => {
                  if (showUpdate) {
                    console.log('⏰ Auto-updating after 10 seconds');
                    handleUpdate();
                  }
                }, 10000);
              }
            });
          }
        });
      });

      // Listen for controller change (new SW activated)
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Reload the page to use the new service worker
        if (!isUpdating) {
          console.log('🔃 Controller changed - reloading page');
          toast.success('Update Complete!', {
            description: 'Loading new version...',
            duration: 1000,
          });
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
      });

      return () => {
        clearInterval(interval);
      };
    }
  }, [isUpdating, showUpdate]);

  const handleUpdate = () => {
    if (!registration || !registration.waiting) {
      console.log('⚠️ No waiting service worker found');
      return;
    }

    console.log('🚀 Activating new service worker');
    setIsUpdating(true);
    
    toast.info('Updating App...', {
      description: 'Installing latest version',
      duration: 2000,
    });

    // Tell the waiting service worker to skip waiting and activate
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  };

  const handleDismiss = () => {
    console.log('⏭️ User dismissed update notification');
    setShowUpdate(false);
    
    // Show again in 2 minutes (more aggressive than before)
    setTimeout(() => {
      console.log('⏰ Reshowing update notification');
      setShowUpdate(true);
    }, 2 * 60 * 1000); // 2 minutes
  };

  if (!showUpdate) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96">
      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-2xl p-5 text-white animate-slide-down">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-white/80 hover:text-white"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-start gap-4">
          <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
            <RefreshCw className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold mb-1">
              New Update Available! 🎉
            </h3>
            <p className="text-sm text-white/90 mb-2">
              Sentlo v10.0.9 with bug fixes and improvements
            </p>
            <p className="text-xs text-white/70 mb-4">
              {isUpdating ? 'Installing update...' : 'Auto-updating in 10 seconds'}
            </p>

            <div className="flex gap-3">
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="flex-1 bg-white text-blue-600 font-bold py-2.5 px-4 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
              >
                {isUpdating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Update Now
                  </>
                )}
              </button>
              <button
                onClick={handleDismiss}
                disabled={isUpdating}
                className="px-3 py-2.5 text-white/90 hover:text-white font-medium text-sm disabled:opacity-50"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Add animation styles
const styles = `
  @keyframes slide-down {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  .animate-slide-down {
    animation: slide-down 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}